/**
 * Calendar.jsx
 * Public NHCA season calendar — Gulf Racing colour scheme
 * URL params: ?year=2025
 * Route: /calendar
 *
 * The "PROVISIONAL" watermark is driven by the API's `confirmed` field
 * (public.season_status, defaults to unconfirmed for a season with no row
 * yet — see migrations/add_season_status_table.sql) rather than a URL flag,
 * so a new season shows it automatically and the Calendar Editor's
 * "Confirm season" toggle is the only thing that turns it off.
 */

import { useState, useEffect } from 'react';

const API_KEY = import.meta.env.VITE_API_KEY || '';

const G = {
  blue:      '#40B4E5',
  blueDark:  '#1a8ab5',
  orange:    '#F47920',
  cream:     '#F5F0E8',
  rowAlt:    '#EBF6FC',
  border:    '#C8E8F5',
  text:      '#1a2a35',
  textLight: '#5a7a8a',
  headerBg:  '#1a2a35',
  dot:       '#F47920',
  dotEmpty:  '#dde8ee',
};

function Dot({ value }) {
  return (
    <div style={{
      width: 12, height: 12, borderRadius: '50%',
      background: value ? G.dot : G.dotEmpty,
      margin: '0 auto',
      boxShadow: value ? `0 0 0 2px #F4792033` : 'none',
    }} />
  );
}

const BOOL_FILTERS = [
  { key: 'open',          label: 'Open'  },
  { key: 'road_legal',    label: 'RL'    },
  { key: 'forgotten_era', label: 'FE'    },
  { key: 'vc',            label: 'V/C'   },
  { key: 'restricted',    label: 'Res'   },
  { key: 'giag',          label: 'GIAG'  },
];

const COLS = [
  { key: 'date',          label: 'Date',      align: 'left',   bool: false },
  { key: 'hill',          label: 'Location',  align: 'left',   bool: false },
  { key: 'open',          label: 'Open',      align: 'center', bool: true  },
  { key: 'road_legal',    label: 'RL',        align: 'center', bool: true  },
  { key: 'forgotten_era', label: 'FE',        align: 'center', bool: true  },
  { key: 'vc',            label: 'V/C',       align: 'center', bool: true  },
  { key: 'restricted',    label: 'Res',       align: 'center', bool: true  },
  { key: 'giag',          label: 'GIAG',      align: 'center', bool: true  },
  { key: 'closing_date',  label: 'Closing',   align: 'left',   bool: false },
  { key: 'regs_date',     label: 'Regs',      align: 'left',   bool: false },
  { key: 'secretary',     label: 'Secretary', align: 'left',   bool: false },
  { key: 'under_16',      label: 'U16',       align: 'center', bool: false },
  { key: 'notes',         label: 'Notes',     align: 'left',   bool: false },
  { key: 'entries',       label: 'Entries',   align: 'center', bool: false },
  { key: 'entry_form',    label: 'Enter',     align: 'center', bool: false },
  { key: 'regs',          label: 'Regs',      align: 'center', bool: false },
  { key: 'results',       label: 'Results',   align: 'center', bool: false },
];

export default function Calendar() {
  const [events, setEvents]         = useState([]);
  const [year, setYear]             = useState('');
  const [provisional, setProvisional] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  // active filters: set of keys that must be true
  const [activeFilters, setActiveFilters] = useState(new Set());

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const y = params.get('year') || new Date().getFullYear().toString();
    setYear(y);

    fetch(`/api/calendar?year=${encodeURIComponent(y)}`, {
      headers: { 'X-API-Key': API_KEY },
    })
      .then(async res => {
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'HTTP ' + res.status);
        return json;
      })
      .then(data => {
        setEvents(data.events || []);
        setProvisional(!data.confirmed);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const toggleFilter = (key) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const filtered = events.filter(ev =>
    [...activeFilters].every(key => ev[key])
  );

  // shared cell padding
  const PAD = '7px 8px';
  const BOOL_PAD = '7px 4px';
  const FONT = '0.92rem';

  const thStyle = (col) => ({
    padding: col.bool ? BOOL_PAD : PAD,
    textAlign: col.align,
    fontWeight: 700,
    fontSize: '0.75rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: G.cream,
    background: G.headerBg,
    borderBottom: `3px solid ${G.orange}`,
    borderRight: `1px solid #2a3d4a`,
    whiteSpace: 'nowrap',
    writingMode: col.bool ? 'vertical-rl' : undefined,
    verticalAlign: col.bool ? 'bottom' : 'middle',
    paddingBottom: col.bool ? '7px' : undefined,
  });

  const tdStyle = (col, alt) => ({
    padding: col.bool ? BOOL_PAD : PAD,
    textAlign: col.align,
    fontSize: FONT,
    color: G.text,
    background: alt ? G.rowAlt : '#fff',
    borderBottom: `1px solid ${G.border}`,
    borderRight: `1px solid ${G.border}`,
    verticalAlign: 'middle',
    whiteSpace: col.key === 'notes' || col.key === 'hill' ? 'normal' : 'nowrap',
  });

  return (
    <div style={{
      fontFamily: "'Roboto Condensed', 'Arial Narrow', Arial, sans-serif",
      background: G.cream,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '0 16px',
      boxSizing: 'border-box',
    }}>

      {/* Google Font */}
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Roboto+Condensed:wght@400;700;900&display=swap" />

      {/* Provisional watermark — fixed to the viewport (not the table) so it
          stays large, centred and visible however tall the table is or how
          far the page is scrolled. Driven by season_status via the API. */}
      {provisional && !loading && (
        <div style={{
          position: 'fixed', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none', zIndex: 1000, overflow: 'hidden',
        }}>
          <div style={{
            fontSize: 'clamp(2.5rem, 11vw, 8rem)',
            fontWeight: 900,
            fontStyle: 'italic',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: `${G.orange}2e`,
            transform: 'rotate(-25deg)',
            userSelect: 'none',
            whiteSpace: 'nowrap',
            textAlign: 'center',
            textShadow: `0 0 60px ${G.orange}33`,
          }}>
            Provisional<br />Calendar
          </div>
        </div>
      )}

      {/* Inner wrapper — sizes to table width so header/filter align with it */}
      <div style={{ display: 'flex', flexDirection: 'column', width: 'fit-content', maxWidth: '100%', minWidth: 0 }}>

      {/* Header */}
      <div style={{
        background: G.headerBg,
        padding: '12px 16px 10px',
        display: 'flex', alignItems: 'center', gap: '12px',
        borderBottom: `4px solid ${G.orange}`,
        width: '100%', boxSizing: 'border-box',
      }}>
        <div style={{
          width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
          background: `conic-gradient(${G.blue} 0deg 180deg, ${G.orange} 180deg 360deg)`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
        }} />
        <div>
          <div style={{ color: G.cream, fontSize: '1.25rem', fontWeight: 900, letterSpacing: '0.06em', lineHeight: 1, textTransform: 'uppercase' }}>
            NHCA {year} Season Calendar
          </div>
          <div style={{ color: G.blue, fontSize: '0.72rem', letterSpacing: '0.1em', marginTop: 2, textTransform: 'uppercase' }}>
            National Hillclimb Association
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{
        background: '#fff',
        borderBottom: `1px solid ${G.border}`,
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexWrap: 'wrap',
        width: '100%', boxSizing: 'border-box',
      }}>
        <span style={{ fontSize: '0.78rem', color: G.textLight, marginRight: 4, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Filter:
        </span>
        {BOOL_FILTERS.map(f => {
          const active = activeFilters.has(f.key);
          return (
            <button
              key={f.key}
              onClick={() => toggleFilter(f.key)}
              style={{
                padding: '3px 10px',
                fontSize: '0.78rem',
                fontWeight: 700,
                fontFamily: 'inherit',
                letterSpacing: '0.04em',
                border: `1.5px solid ${active ? G.orange : G.border}`,
                borderRadius: 3,
                background: active ? G.orange : '#fff',
                color: active ? '#fff' : G.textLight,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {f.label}
            </button>
          );
        })}
        <span style={{
          fontSize: '0.78rem',
          color: G.textLight,
          fontWeight: 700,
        }}>
          {filtered.length} event{filtered.length !== 1 ? 's' : ''}
          {activeFilters.size > 0 && ` of ${events.length}`}
        </span>
      </div>

      {/* Table */}
      <div style={{ padding: '12px 0', width: '100%', boxSizing: 'border-box' }}>

        {loading && (
          <div style={{ textAlign: 'center', padding: '3rem', color: G.textLight }}>
            Loading calendar…
          </div>
        )}

        {error && (
          <div style={{ background: '#fff0f0', border: '1px solid #ffaaaa', borderRadius: 6, padding: '1rem', color: '#c00' }}>
            ⚠️ {error}
          </div>
        )}

        {!loading && !error && events.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem', color: G.textLight }}>
            No events found for {year}.
          </div>
        )}

        {!loading && !error && events.length > 0 && (
          <div style={{ position: 'relative', display: 'block', maxWidth: '100%' }}>

            <div style={{ overflowX: 'auto' }}>
              <table style={{
                borderCollapse: 'collapse',
                boxShadow: '0 2px 12px rgba(26,42,53,0.10)',
                borderRadius: 6,
                overflow: 'hidden',
                tableLayout: 'auto',
              }}>
                <thead>
                  <tr>
                    {COLS.map(col => (
                      <th key={col.key} style={thStyle(col)}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={COLS.length} style={{ padding: '1.5rem', textAlign: 'center', color: G.textLight, background: '#fff', fontSize: FONT }}>
                        No events match the selected filters.
                      </td>
                    </tr>
                  )}
                  {filtered.map((ev, i) => (
                    <tr key={ev.calendar_id || i}
                      onMouseEnter={e => { e.currentTarget.querySelectorAll('td').forEach(td => td.style.background = '#fffae8'); }}
                      onMouseLeave={e => { e.currentTarget.querySelectorAll('td').forEach(td => td.style.background = i % 2 === 1 ? G.rowAlt : '#fff'); }}
                    >
                      {COLS.map(col => {
                        const alt = i % 2 === 1;

                        if (col.key === 'date') {
                          // Format as "Sat 14 Jun" — no year, compact for mobile
                          const d = ev.date_raw ? new Date(ev.date_raw) : null;
                          const fmt = d ? d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : ev.date || '—';
                          return (
                            <td key="date" style={{ ...tdStyle(col, alt), fontWeight: 700, color: G.blueDark }}>
                              {fmt}
                            </td>
                          );
                        }

                        if (col.key === 'entries') {
                          return (
                            <td key="entries" style={tdStyle(col, alt)}>
                              {ev.published ? (
                                <a
                                  href={`/entries/${ev.calendar_id}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    background: G.orange,
                                    color: '#fff',
                                    borderRadius: 3,
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    textDecoration: 'none',
                                    letterSpacing: '0.04em',
                                    textTransform: 'uppercase',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  View →
                                </a>
                              ) : null}
                            </td>
                          );
                        }

                        if (col.bool) {
                          return (
                            <td key={col.key} style={tdStyle(col, alt)}>
                              <Dot value={ev[col.key]} />
                            </td>
                          );
                        }

                        if (col.key === 'entry_form') {
                          const entryHref = ev.entry_list_ready ? (ev.entry_form_url ||
                            (ev.has_entry_form_pdf ? `/api/calendar-editor/events/${ev.calendar_id}/pdf/entry-form` : null)) : null;
                          return (
                            <td key="entry_form" style={tdStyle(col, alt)}>
                              {entryHref ? (
                                <a href={entryHref} target="_blank" rel="noreferrer"
                                  style={{ display: 'inline-block', padding: '2px 8px', background: G.blueDark, color: '#fff', borderRadius: 3, fontSize: '0.75rem', fontWeight: 700, textDecoration: 'none', letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                                  Enter →
                                </a>
                              ) : null}
                            </td>
                          );
                        }

                        if (col.key === 'regs') {
                          const regsHref = ev.regs_url ||
                            (ev.has_regs_pdf ? `/api/calendar-editor/events/${ev.calendar_id}/pdf/regs` : null);
                          return (
                            <td key="regs" style={tdStyle(col, alt)}>
                              {regsHref ? (
                                <a href={regsHref} target="_blank" rel="noreferrer"
                                  style={{ display: 'inline-block', padding: '2px 8px', background: G.blueDark, color: '#fff', borderRadius: 3, fontSize: '0.75rem', fontWeight: 700, textDecoration: 'none', letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                                  Regs →
                                </a>
                              ) : null}
                            </td>
                          );
                        }

                        if (col.key === 'results') {
                          return (
                            <td key="results" style={tdStyle(col, alt)}>
                              {ev.results_url ? (
                                <a href={ev.results_url} target="_blank" rel="noreferrer"
                                  style={{ display: 'inline-block', padding: '2px 8px', background: G.blueDark, color: '#fff', borderRadius: 3, fontSize: '0.75rem', fontWeight: 700, textDecoration: 'none', letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                                  Results →
                                </a>
                              ) : null}
                            </td>
                          );
                        }

                        return (
                          <td key={col.key} style={tdStyle(col, alt)}>
                            {ev[col.key] || ''}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      </div>{/* end inner wrapper */}
    </div>
  );
}
